// Change Password — self-serve rotation from the Profile page.
// POSTs to /api/auth/change-password (requires current password, min 6 chars,
// must differ from current). Backend rotates the session so other devices sign
// out while THIS device stays logged in via fresh cookies + tokens.
import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChangePasswordCard() {
  const { authAxios } = useAuth();
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null); // {type: 'ok'|'err', text}
  const [expanded, setExpanded] = useState(false);
  const gold = "#C9A227";

  const reset = () => { setCur(""); setNext(""); setConfirm(""); };

  const submit = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (!cur || !next) { setMsg({ type: "err", text: "Please fill both password fields." }); return; }
    if (next.length < 6) { setMsg({ type: "err", text: "New password must be at least 6 characters." }); return; }
    if (next !== confirm) { setMsg({ type: "err", text: "New password and confirmation don't match." }); return; }
    if (next === cur) { setMsg({ type: "err", text: "New password must be different from your current password." }); return; }
    setBusy(true);
    try {
      await authAxios.post(`${API}/auth/change-password`, { current_password: cur, new_password: next });
      setMsg({ type: "ok", text: "Password updated. Other devices have been signed out." });
      reset();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Something went wrong. Please try again.";
      setMsg({ type: "err", text: typeof detail === "string" ? detail : "Password change failed." });
    } finally {
      setBusy(false);
    }
  };

  const input = {
    width: "100%", padding: "10px 12px", background: "#0D0F13",
    border: "1px solid #1E222A", color: "#eef2f7", borderRadius: 6,
    fontSize: 13, fontFamily: "inherit", outline: "none",
  };

  return (
    <div className="flexx-card" data-testid="change-password-card" style={{ padding: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: expanded ? 14 : 0 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#eef2f7", letterSpacing: ".04em" }}>🔑 Change Password</div>
          <div style={{ fontSize: 11.5, color: "#6B7280", marginTop: 4, lineHeight: 1.6 }}>
            Rotate your password. All other devices will be signed out.
          </div>
        </div>
        <button
          data-testid="change-password-toggle-btn"
          onClick={() => { setExpanded((v) => !v); setMsg(null); reset(); }}
          style={{ fontSize: 11, padding: "6px 12px", background: expanded ? "transparent" : gold, color: expanded ? gold : "#000", border: `1px solid ${gold}`, borderRadius: 4, cursor: "pointer", fontFamily: "monospace", fontWeight: 700, letterSpacing: 1 }}
        >
          {expanded ? "CANCEL" : "CHANGE"}
        </button>
      </div>

      {expanded && (
        <form onSubmit={submit} style={{ display: "grid", gap: 10 }}>
          <label style={{ display: "grid", gap: 5 }}>
            <span style={{ fontSize: 10.5, color: "#6B7280", letterSpacing: 1, fontFamily: "monospace" }}>CURRENT PASSWORD</span>
            <input data-testid="change-password-current-input" type="password" autoComplete="current-password" value={cur} onChange={(e) => setCur(e.target.value)} style={input} />
          </label>
          <label style={{ display: "grid", gap: 5 }}>
            <span style={{ fontSize: 10.5, color: "#6B7280", letterSpacing: 1, fontFamily: "monospace" }}>NEW PASSWORD (min 6)</span>
            <input data-testid="change-password-new-input" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} style={input} />
          </label>
          <label style={{ display: "grid", gap: 5 }}>
            <span style={{ fontSize: 10.5, color: "#6B7280", letterSpacing: 1, fontFamily: "monospace" }}>CONFIRM NEW PASSWORD</span>
            <input data-testid="change-password-confirm-input" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} style={input} />
          </label>

          {msg && (
            <div
              data-testid={msg.type === "ok" ? "change-password-success" : "change-password-error"}
              style={{
                fontSize: 12.5, padding: "10px 12px", borderRadius: 6,
                background: msg.type === "ok" ? "rgba(34,197,94,0.08)" : "rgba(255,68,68,0.08)",
                border: `1px solid ${msg.type === "ok" ? "#22c55e55" : "#ff444455"}`,
                color: msg.type === "ok" ? "#4ade80" : "#ff9aa6",
                lineHeight: 1.5,
              }}
            >
              {msg.text}
            </div>
          )}

          <button
            data-testid="change-password-submit-btn"
            type="submit"
            disabled={busy}
            style={{
              padding: "11px", background: gold, border: `1px solid ${gold}`,
              borderRadius: 6, color: "#000", fontSize: 12.5, fontWeight: 700,
              letterSpacing: ".04em", cursor: busy ? "wait" : "pointer",
              opacity: busy ? 0.6 : 1, fontFamily: "monospace",
            }}
          >
            {busy ? "UPDATING…" : "UPDATE PASSWORD"}
          </button>
        </form>
      )}
    </div>
  );
}
